import React from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { ButtonIconSeeker, CardSeeker, IconSeeker } from '@ui-seeker/core'
import { postSubmit } from '@ui-seeker/utils'
import { useToast } from '@hooks'
import { BACKEND_APIS } from '@constants'

import { customerDetailsFilters, reloadCustomerModulesFilters } from '../@signals'
import { useCustomerModules } from './@hooks'


const MODULE_ICONS = {
    digitalShelf:     'layer-group',
    shareOfSearch:    'magnifying-glass-chart',
    stock:            'boxes-stacked',
    productPages:     'file-lines',
    pricing:          'tag',
    marketplaces:     'store',
    admin:            'shield-halved',
    skuDetails:       'barcode',
    analytic:         'chart-mixed',
    reports:          'chart-bar',
    customDashboards: 'gauge',
    banners:          'rectangle-ad',
    catalogue:        'book-open',
    infocenter:       'circle-info',
    monitoring:       'eye',
    monitoringAdmin:  'eye-slash',
}


export default function CustomerModulesPanel() {

    const { assignedModules, allModules, isLoading } = useCustomerModules()
    const { showToast } = useToast()

    if (!assignedModules.length && !allModules.length) return <></>

    const availableModules = allModules.filter(m => !assignedModules.includes(m.value))

    const handleAdd = module => {
        showToast.promise(
            postSubmit(`${BACKEND_APIS.MANAGEMENT}/customer/customer-module-add`, {
                ...customerDetailsFilters.value,
                module
            }).then(() => {
                reloadCustomerModulesFilters.value = true
            }),
            { success: 'Module added', loading: 'Adding...' }
        )
    }

    const handleDelete = module => {
        showToast.confirm('Remove this module from the customer?', {
            onSubmit: () => {
                showToast.promise(
                    postSubmit(`${BACKEND_APIS.MANAGEMENT}/customer/customer-module-delete`, {
                        ...customerDetailsFilters.value,
                        module
                    }).then(() => {
                        reloadCustomerModulesFilters.value = true
                    }),
                    { success: 'Module removed', loading: 'Removing...' }
                )
            }
        })
    }

    const moduleLabel = value => allModules.find(m => m.value === value)?.label ?? value

    const cardTitle = <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2">
            Modules — {customerDetailsFilters.value?.name}
        </Typography>
        <Chip
            label={assignedModules.length}
            size="small"
            color="primary"
            sx={{ height: 18, fontSize: '0.7rem', ml: 0.5 }}
        />
    </Box>

    const cardActions = <ButtonIconSeeker
        icon="circle-xmark"
        tooltip="Close"
        onClick={() => { customerDetailsFilters.value = {} }}
    />

    return (
        <Box id="customer-modules-panel">
            <CardSeeker title={cardTitle} actions={cardActions} isLoading={isLoading}>
                <Stack spacing={2.5}>

                    <Stack direction="row" flexWrap="wrap" gap={1}>
                        {assignedModules.map(module => (
                            <Chip
                                key={module}
                                icon={
                                    <IconSeeker
                                        icon={MODULE_ICONS[module] ?? 'circle-dot'}
                                        sx={{ fontSize: '0.85rem !important', ml: '10px !important' }}
                                    />
                                }
                                label={moduleLabel(module)}
                                onDelete={() => handleDelete(module)}
                                sx={{
                                    height: 34,
                                    fontSize: '0.8rem',
                                    backgroundColor: 'disabled.dark',
                                    fontWeight: 600,
                                    '& .MuiChip-deleteIcon': {
                                        '&:hover': { color: 'error.main' }
                                    }
                                }}
                            />
                        ))}
                        {!assignedModules.length && (
                            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                No modules assigned yet
                            </Typography>
                        )}
                    </Stack>

                    {availableModules.length > 0 && (
                        <Stack
                            spacing={1}
                            sx={{ pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                <IconSeeker icon="circle-plus" sx={{ mr: 0.75, fontSize: '0.75rem' }} />
                                Available modules
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {availableModules.map(m => (
                                    <Chip
                                        key={m.value}
                                        icon={
                                            <IconSeeker
                                                icon={MODULE_ICONS[m.value] ?? 'circle-dot'}
                                                sx={{ fontSize: '0.85rem !important', ml: '10px !important' }}
                                            />
                                        }
                                        label={m.label}
                                        onClick={() => handleAdd(m.value)}
                                        variant="outlined"
                                        sx={{
                                            height: 34,
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            fontWeight: 500,
                                            borderColor: 'text.disabled',
                                            color: 'text.secondary',
                                            '&:hover': {
                                                backgroundColor: 'disabled.dark',
                                                borderColor: 'text.primary',
                                                color: 'text.primary',
                                            }
                                        }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    )}

                </Stack>
            </CardSeeker>
        </Box>
    )
}
